/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { css as compiledCss } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider } from 'react-intl';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import { useProvider } from '@atlaskit/editor-common/provider-factory';
import Heading from '@atlaskit/heading/heading';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import ProjectIcon from '@atlaskit/icon/core/project';
import UndoIcon from '@atlaskit/icon/core/undo';
import IconTile from '@atlaskit/icon/icon-tile';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Mention from '@atlaskit/mention/mention';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import type ProfileClient from '@atlaskit/profilecard/profile-card-client';
import ProfileCardTrigger from '@atlaskit/profilecard/profile-card-trigger';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/utils/teams-app-navigation/navigate-to-teams-app';
import { token } from '@atlaskit/tokens';
import type {
	HoverEntityCardClickTarget,
	EntityAnnotationOpenMethod,
	InferredProjectPromotionOutcome,
	InferredProjectPromotionFailure,
} from '@atlassian/conversation-assistant-instrumentation/controllers/analytics/entity-annotation-events';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import {
	type ProfileTriggerRenderer,
	useProfileTrigger,
} from '../../controllers/profile-trigger/ProfileTriggerContext';
import messages from './inferredProjectCardMessages';
import type { InferredProjectPerson } from './parseInferredProjectContext';
import { PromoteInferredProjectSection } from './PromoteInferredProjectSection';
import { useInferredProjectContext } from './useInferredProjectContext';
import { usePromoteInferredProject } from './usePromoteInferredProject';

const COMPACT_CONTRIBUTOR_LIMIT = 4;
const HOVER_CLOSE_DELAY_MS = 150;

const projectTitleButtonStyles = compiledCss({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	boxDecorationBreak: 'clone',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'inline',
	font: token('font.body'),
	overflowWrap: 'break-word',
	paddingBlock: '3px',
	paddingInlineEnd: '2px',
	paddingInlineStart: '2px',
	WebkitBoxDecorationBreak: 'clone',
	whiteSpace: 'normal',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width'),
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

const projectIconStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	marginInlineEnd: token('space.025'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const projectTitleStyles = compiledCss({
	minWidth: 0,
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

const projectLabelStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	marginInlineStart: token('space.050'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.medium'),
		maxWidth: '375px',
		minWidth: '320px',
	},
	basedOn: {
		backgroundColor: token('elevation.surface.sunken'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- The design requires a static gradient-and-grid treatment; its colors use design tokens.
		backgroundImage: `linear-gradient(270deg, rgba(248, 248, 248, 0) 0%, ${token('elevation.rovo.surface.overlay')} 100%), linear-gradient(${token('color.border.disabled')} 1px, transparent 1px), linear-gradient(90deg, ${token('color.border.disabled')} 1px, transparent 1px)`,
		backgroundPosition: '0 0, 6px 4px, 6px 4px',
		backgroundSize: '100% 100%, 24px 24px, 24px 24px',
		borderRadius: token('radius.medium'),
	},
	flippedIcon: {
		display: 'inline-flex',
		transform: 'rotate(180deg)',
	},
	sourceLink: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
	},
	contributors: {
		paddingBlockStart: token('space.100'),
	},
	contributorLink: {
		textDecoration: 'none',
	},
	showAllTrigger: {
		backgroundColor: 'transparent',
		paddingInline: '0',
	},
});

export type InferredProjectInlineCardProps = {
	canonicalProjectName?: string;
	cloudId: string;
	fallback: React.ReactNode;
	projectId: string;
	projectName: string;
	/** Called when the project hover card opens. */
	onOpen?: (method: EntityAnnotationOpenMethod) => void;
	onPromotionResult?: (
		outcome: InferredProjectPromotionOutcome,
		failure?: InferredProjectPromotionFailure,
	) => void;
	onInteraction?: (targetType: HoverEntityCardClickTarget) => void;
};

const getAccountId = (ari: string | undefined): string | undefined => {
	const match = ari?.match(/^ari:cloud:identity::user\/(.+)$/);
	return match?.[1];
};

type ContributorChipProps = {
	cloudId: string;
	locale: string;
	person: InferredProjectPerson;
	profileClient: ProfileClient | null;
	renderProfileTrigger?: ProfileTriggerRenderer;
	onClick?: () => void;
};

const ContributorChip = ({
	cloudId,
	locale,
	person,
	profileClient,
	renderProfileTrigger,
	onClick,
}: ContributorChipProps): React.ReactNode => {
	const pill = (
		<IntlProvider locale={locale}>
			<Mention
				avatarUrl={person.avatarUrl}
				id={person.ari ?? person.name}
				renderAvatarSlot={Boolean(person.avatarUrl)}
				text={`@${person.name}`}
			/>
		</IntlProvider>
	);
	const accountId = getAccountId(person.ari);

	if (!accountId) {
		return pill;
	}

	if (fg('rovo_chat_inferred_project_hover_cards')) {
		const { href } = navigateToTeamsApp({
			type: 'USER',
			payload: { userId: accountId },
			cloudId,
		});

		return (
			<Anchor
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				xcss={styles.contributorLink}
				onClick={onClick}
				onAuxClick={(event) => {
					if (event.button === 1) {
						onClick?.();
					}
				}}
			>
				{pill}
			</Anchor>
		);
	}

	if (renderProfileTrigger) {
		return renderProfileTrigger({ userId: accountId, cloudId, children: pill });
	}

	if (profileClient) {
		return (
			<ProfileCardTrigger
				ariaLabel={person.name}
				cloudId={cloudId}
				resourceClient={profileClient}
				userId={accountId}
			>
				{pill}
			</ProfileCardTrigger>
		);
	}

	return pill;
};

const dedupeContributors = (people: readonly InferredProjectPerson[]): InferredProjectPerson[] => {
	const seen = new Set<string>();
	return people.filter((person) => {
		const key = person.ari ?? person.name;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

type InferredProjectCardContentProps = Omit<InferredProjectInlineCardProps, 'fallback'>;

const InferredProjectCardInner = ({
	canonicalProjectName,
	cloudId,
	projectId,
	projectName,
	onOpen,
	onInteraction,
	onPromotionResult,
}: InferredProjectCardContentProps): React.ReactNode => {
	const { formatMessage, locale } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { load, ...state } = useInferredProjectContext({ cloudId, projectId });
	const promotion = usePromoteInferredProject(cloudId, onPromotionResult);
	const hasPromotedProject = promotion.state.status === 'promoted';
	const [isShowingAllContributors, setIsShowingAllContributors] = useState(false);
	const [isSourcesOpen, setIsSourcesOpen] = useState(false);
	const profilecardProviderPromise = useProvider('profilecardProvider');
	const renderProfileTrigger = useProfileTrigger();
	const [profileClient, setProfileClient] = useState<ProfileClient | null>(null);

	useEffect(() => {
		let isMounted = true;
		profilecardProviderPromise?.then(({ resourceClient }) => {
			if (isMounted && resourceClient) {
				setProfileClient(resourceClient as ProfileClient);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [profilecardProviderPromise]);

	useEffect(
		() => () => {
			if (closeTimer.current) {
				clearTimeout(closeTimer.current);
			}
		},
		[],
	);

	const open = useCallback(
		(method: EntityAnnotationOpenMethod) => {
			if (!isOpen) {
				load();
				setIsOpen(true);
				onOpen?.(method);
			}
		},
		[isOpen, load, onOpen],
	);
	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		setIsOpen(false);
	}, []);
	const cancelScheduledClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);
	const handleTriggerMouseEnter = useCallback(() => {
		cancelScheduledClose();
		open('hover');
	}, [cancelScheduledClose, open]);
	const handleTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) {
				closeAndUnlock();
			}
		}, HOVER_CLOSE_DELAY_MS);
	}, [closeAndUnlock]);
	const handleTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}

		lockedByClick.current = true;
		open('click');
	}, [closeAndUnlock, isOpen, open]);
	const handleContentMouseEnter = useCallback(() => {
		cancelScheduledClose();
	}, [cancelScheduledClose]);
	const handleContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) {
			closeAndUnlock();
		}
	}, [closeAndUnlock]);
	const handleContentBlur = useCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				handleContentMouseLeave();
			}
		},
		[handleContentMouseLeave],
	);

	const context = state.status === 'success' ? state.data : undefined;
	const title = context?.title ?? canonicalProjectName ?? projectName;
	const owner = context?.owners[0];
	const suggestedContributors = dedupeContributors([
		...(context?.owners ?? []),
		...(context?.collaborators ?? []),
	]);
	const confirmedContributors = dedupeContributors([
		...(context?.owners.slice(1) ?? []),
		...(context?.collaborators ?? []),
	]);
	const contributors = hasPromotedProject ? confirmedContributors : suggestedContributors;
	const hasMoreContributors = contributors.length > COMPACT_CONTRIBUTOR_LIMIT;
	const visibleContributors = isShowingAllContributors
		? contributors
		: contributors.slice(0, COMPACT_CONTRIBUTOR_LIMIT);
	const source = context?.sources[0];

	// Uses inline button semantics so long project names wrap within response text.
	return (
		<Popup
			isOpen={isOpen}
			onClose={closeAndUnlock}
			placement="bottom-start"
			content={() => (
				<Box
					onBlur={handleContentBlur}
					onFocus={handleContentMouseEnter}
					onMouseEnter={handleContentMouseEnter}
					onMouseLeave={handleContentMouseLeave}
					paddingBlockEnd="space.200"
					paddingBlockStart="space.150"
					paddingInline="space.200"
					xcss={styles.card}
				>
					<Stack space="space.100">
						<Stack space="space.025">
							<Heading color="color.text" size="xsmall">
								{title}
							</Heading>
							<Text color="color.text.subtlest" size="small">
								<TeamworkGraphIcon color={token('color.icon.subtlest')} label="" size="small" />{' '}
								{formatMessage(hasPromotedProject ? messages.project : messages.inferredProject)}
							</Text>
						</Stack>
						{state.status === 'loading' ? <Text>{formatMessage(messages.loading)}</Text> : null}
						{state.status === 'error' ? <Text>{formatMessage(messages.loadError)}</Text> : null}
						{context ? (
							<Stack space="space.100">
								{hasPromotedProject && source ? (
									<Stack space="space.100">
										<Button
											aria-expanded={isSourcesOpen}
											onClick={() => setIsSourcesOpen((isOpen) => !isOpen)}
											shouldFitContainer
										>
											{formatMessage(messages.sourcesAndRelatedLinks)}
										</Button>
										{isSourcesOpen ? (
											<Box xcss={styles.sourceLink}>
												<LinkList
													openLinksInNewTab={fg('rovo_chat_inferred_project_hover_cards')}
													onLinkClick={(_url, _index, options) => {
														if (!options?.isContextMenu) {
															onInteraction?.('source_document');
														}
													}}
													size="compact"
													titles={context.sources.map((item) => item.title ?? '')}
													urls={context.sources.map((item) => item.url)}
												/>
											</Box>
										) : null}
									</Stack>
								) : source ? (
									<Box padding="space.100" xcss={styles.basedOn}>
										<Stack space="space.100">
											<Text color="color.text.subtlest" size="small">
												<span css={styles.flippedIcon}>
													<UndoIcon color={token('color.icon.subtlest')} label="" size="small" />
												</span>{' '}
												{formatMessage(messages.sourcedFrom)}
											</Text>
											<Box xcss={styles.sourceLink}>
												<LinkList
													openLinksInNewTab={fg('rovo_chat_inferred_project_hover_cards')}
													onLinkClick={(_url, _index, options) => {
														if (!options?.isContextMenu) {
															onInteraction?.('source_document');
														}
													}}
													expandedMaxSources={1}
													maxSources={1}
													showExpand={false}
													size="compact"
													titles={source.title ? [source.title] : undefined}
													urls={[source.url]}
												/>
											</Box>
										</Stack>
									</Box>
								) : null}
								{context.summary ? (
									<Text color="color.text.subtle" size="medium">
										{context.summary}
									</Text>
								) : null}
								{hasPromotedProject && owner ? (
									<Stack space="space.075">
										<Text color="color.text" size="small" weight="medium">
											{formatMessage(messages.owner)}
										</Text>
										<Inline shouldWrap space="space.075">
											<ContributorChip
												onClick={() => onInteraction?.('person_profile')}
												cloudId={cloudId}
												locale={locale}
												person={owner}
												profileClient={profileClient}
												renderProfileTrigger={renderProfileTrigger}
											/>
										</Inline>
									</Stack>
								) : null}
								<Stack space="space.150" xcss={styles.contributors}>
									<Inline alignBlock="center" spread="space-between">
										<Text color="color.text" size="small" weight="medium">
											{formatMessage(
												hasPromotedProject ? messages.contributors : messages.suggestedContributors,
											)}
										</Text>
										{hasMoreContributors && !isShowingAllContributors ? (
											<Pressable
												onClick={() => setIsShowingAllContributors(true)}
												xcss={styles.showAllTrigger}
											>
												<Text color="color.text.subtle" size="small">
													{formatMessage(messages.showAllContributors)}
												</Text>
											</Pressable>
										) : null}
									</Inline>
									{visibleContributors.length ? (
										<Inline shouldWrap space="space.075">
											{visibleContributors.map((contributor) => (
												<ContributorChip
													onClick={() => onInteraction?.('person_profile')}
													cloudId={cloudId}
													locale={locale}
													key={contributor.ari ?? contributor.name}
													person={contributor}
													profileClient={profileClient}
													renderProfileTrigger={renderProfileTrigger}
												/>
											))}
										</Inline>
									) : (
										<Text color="color.text.subtle">
											{formatMessage(
												hasPromotedProject
													? messages.noContributors
													: messages.noSuggestedContributors,
											)}
										</Text>
									)}
								</Stack>
								<ErrorBoundary fallback={null}>
									<Suspense fallback={null}>
										<PromoteInferredProjectSection
											onInteraction={onInteraction}
											contributors={suggestedContributors}
											inferredProjectAri={projectId}
											promote={(ari) => {
												onInteraction?.('claim_project');
												promotion.promote(ari);
											}}
											state={promotion.state}
										/>
									</Suspense>
								</ErrorBoundary>
							</Stack>
						) : null}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button -- The project treatment must wrap with response text, which standard buttons cannot support.
				<span
					{...triggerProps}
					aria-label={formatMessage(
						hasPromotedProject ? messages.projectTrigger : messages.inferredProjectTrigger,
						{ projectName },
					)}
					css={projectTitleButtonStyles}
					onClick={handleTriggerClick}
					onKeyDown={(event) => {
						if (
							event.key === 'Enter' ||
							event.key === ' ' ||
							event.key === 'Space' ||
							event.key === 'Spacebar' ||
							event.code === 'Space'
						) {
							event.preventDefault();
							handleTriggerClick();
						}
					}}
					onBlur={handleTriggerMouseLeave}
					onFocus={handleTriggerMouseEnter}
					onMouseEnter={handleTriggerMouseEnter}
					onMouseLeave={handleTriggerMouseLeave}
					role="button"
					tabIndex={0}
				>
					<span>
						<span css={projectIconStyles}>
							<IconTile appearance="purple" icon={ProjectIcon} label="" size="xsmall" />
						</span>
						<span css={projectTitleStyles}>{projectName}</span>
					</span>
					<span css={projectLabelStyles}>
						<Lozenge appearance="discovery">
							<Inline space="space.050" alignBlock="center">
								<TeamworkGraphIcon color={token('color.icon.subtlest')} label="" size="small" />
								{formatMessage(hasPromotedProject ? messages.project : messages.inferredProject)}
							</Inline>
						</Lozenge>
					</span>
				</span>
			)}
		/>
	);
};

export const InferredProjectInlineCard = ({
	fallback,
	...props
}: InferredProjectInlineCardProps): React.ReactNode => (
	<ErrorBoundary fallback={<>{fallback}</>}>
		<AtlassianRelayEnvironmentProvider>
			<InferredProjectCardInner key={`${props.cloudId}:${props.projectId}`} {...props} />
		</AtlassianRelayEnvironmentProvider>
	</ErrorBoundary>
);
